import { useEffect, useState } from 'react';
import { AlertTriangle, HardDriveDownload, X } from 'lucide-react';
import { localStore } from '../db';
import { flushOutbox, getPendingCounts, pullAll } from '../sync';
import { flushAllLiveDocs } from '../lib/yjsCollab';

type Stage =
  | 'measuring' // pre-flushing live docs + outbox to drive the count to 0
  | 'safe' // nothing pending — single-click confirm
  | 'unsafe' // pending writes still present — typed-confirmation required
  | 'wiping' // user confirmed; wipe + pullAll in progress
  | 'error'; // wipe or pullAll threw

const CONFIRM_PHRASE = 'DISCARD';

/**
 * "Resync from Server" modal. Replaces the bare `window.confirm` that used to
 * front the destructive `localStore.wipe()` + `pullAll()` sequence — the old
 * confirm warned "any unsynced edits will be lost" but didn't actually
 * inspect what was pending, so a user who believed (correctly, per the green
 * status light) that everything was synced could destroy IDB-only writes
 * that the outbox had silently dropped as permanent errors.
 *
 * Flow:
 *   1. measuring — flushAllLiveDocs + flushOutbox once, then re-read
 *      getPendingCounts. Best chance to drain anything that CAN drain
 *      before the user is asked.
 *   2. safe — counts are zero; show a single-click confirm.
 *   3. unsafe — counts are non-zero; list them and require the user to
 *      type DISCARD before the wipe button enables.
 *   4. wiping — localStore.wipe() then pullAll(). On success, close.
 */
export function ResyncFromServerModal({
  onClose,
  onComplete,
}: {
  onClose: () => void;
  onComplete: () => void;
}) {
  const [stage, setStage] = useState<Stage>('measuring');
  const [counts, setCounts] = useState<{
    pendingDocCount: number;
    pendingCustomerCount: number;
    outboxOpCount: number;
  }>({ pendingDocCount: 0, pendingCustomerCount: 0, outboxOpCount: 0 });
  const [confirmInput, setConfirmInput] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && stage !== 'wiping') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, stage]);

  // Measure phase: try to drain everything that can drain, then re-read
  // pending counts. Settles into either 'safe' or 'unsafe' stage.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Best-effort flush; any genuine failure means the corresponding row
        // stays pendingSync:true and surfaces in the count below.
        await flushAllLiveDocs();
        await flushOutbox();
      } catch (err) {
        console.warn('[vault] pre-resync flush failed', err);
      }
      if (cancelled) return;
      try {
        const c = await getPendingCounts();
        if (cancelled) return;
        setCounts(c);
        const total = c.pendingDocCount + c.pendingCustomerCount + c.outboxOpCount;
        setStage(total === 0 ? 'safe' : 'unsafe');
      } catch (err) {
        if (cancelled) return;
        console.warn('[vault] getPendingCounts failed', err);
        // If we can't measure, treat as unsafe — better a friction-y prompt
        // than a silent wipe.
        setStage('unsafe');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const total = counts.pendingDocCount + counts.pendingCustomerCount + counts.outboxOpCount;
  const canConfirmUnsafe = confirmInput.trim().toUpperCase() === CONFIRM_PHRASE;

  async function performWipe() {
    setStage('wiping');
    setErrorMessage(null);
    try {
      await localStore.wipe();
      await pullAll();
      onComplete();
    } catch (err: any) {
      console.warn('[vault] resync from server failed', err);
      setErrorMessage(err?.body?.error ?? err?.message ?? 'Unable to resync.');
      setStage('error');
    }
  }

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
      <div className="card w-full max-w-md p-5">
        <div className="flex items-center gap-2 mb-3">
          <HardDriveDownload className="text-dragon-500" size={18} />
          <h2 className="font-semibold">Resync from server</h2>
          {stage !== 'wiping' && (
            <button onClick={onClose} className="btn-ghost ml-auto !p-1" aria-label="Close">
              <X size={14} />
            </button>
          )}
        </div>

        {stage === 'measuring' && (
          <p className="text-sm text-ink-300">
            Flushing pending edits and checking what's still local…
          </p>
        )}

        {stage === 'safe' && (
          <div className="space-y-3">
            <p className="text-sm text-ink-300">
              No edits are waiting to sync. This will clear your local cache
              and reload everything from the server. Use this if your local
              cache looks corrupted or out of date.
            </p>
            <div className="flex gap-2">
              <button className="btn-ghost flex-1" onClick={onClose}>
                Cancel
              </button>
              <button className="btn-primary flex-1" onClick={performWipe}>
                Resync
              </button>
            </div>
          </div>
        )}

        {stage === 'unsafe' && (
          <div className="space-y-3">
            <div className="flex items-start gap-2 rounded border border-amber-700 bg-amber-950/40 p-3 text-sm text-amber-200">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" aria-hidden />
              <div>
                <strong>
                  {total === 1
                    ? '1 unsynced change will be lost'
                    : `${total} unsynced changes will be lost`}
                </strong>
                <ul className="mt-2 list-disc pl-4 space-y-0.5 text-amber-100/90">
                  {counts.pendingDocCount > 0 && (
                    <li>
                      {counts.pendingDocCount} page
                      {counts.pendingDocCount === 1 ? '' : 's'} not yet on the
                      server
                    </li>
                  )}
                  {counts.pendingCustomerCount > 0 && (
                    <li>
                      {counts.pendingCustomerCount} customer
                      {counts.pendingCustomerCount === 1 ? '' : 's'} not yet on
                      the server
                    </li>
                  )}
                  {counts.outboxOpCount > 0 && (
                    <li>
                      {counts.outboxOpCount} queued operation
                      {counts.outboxOpCount === 1 ? '' : 's'} (creates,
                      renames, deletes) not yet sent
                    </li>
                  )}
                </ul>
                <p className="mt-2 text-amber-100/90">
                  These exist only in this browser. Resyncing now will destroy
                  them. Cancel and check your network — they may flush on
                  their own.
                </p>
              </div>
            </div>
            <label className="block text-sm text-ink-300">
              Type <span className="font-mono font-semibold text-ink-100">{CONFIRM_PHRASE}</span> to
              continue anyway:
            </label>
            <input
              type="text"
              className="input"
              value={confirmInput}
              onChange={(e) => setConfirmInput(e.target.value)}
              autoFocus
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="characters"
              spellCheck={false}
            />
            <div className="flex gap-2">
              <button className="btn-ghost flex-1" onClick={onClose}>
                Cancel
              </button>
              <button
                className="btn-primary flex-1 disabled:opacity-50"
                disabled={!canConfirmUnsafe}
                onClick={performWipe}
              >
                Discard and resync
              </button>
            </div>
          </div>
        )}

        {stage === 'wiping' && (
          <p className="text-sm text-ink-300">Wiping local cache and reloading…</p>
        )}

        {stage === 'error' && (
          <div className="space-y-3">
            <div className="rounded border border-red-900 bg-red-950/40 p-3 text-sm text-red-300">
              {errorMessage ?? 'Resync failed.'}
            </div>
            <button className="btn-ghost w-full" onClick={onClose}>
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
